import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Asset, SceneGroup } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { intToHex } from 'js/src/libs/color-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';

/**
 * A short, video-level summary of a reference: which designs it uses, whether it uses on-screen
 * text at all, whether it captions its voiceover, and which scene closes it.
 *
 * Deliberately global. Per-asset styling — which size, color or alignment a given asset carries and
 * whether that was a choice or the design's default — is reported in the annotation itself, on the
 * property line, by the `changed from design default` markers. That comparison is both more precise
 * than any summary could be and general across every property, so nothing here tries to
 * characterize individual assets.
 */

interface SceneSummary {
    design: string;
    category?: string;
    texts: { name: string; text: string; size: number }[];
}

/** The scenes' designs and text assets, read off the loaded reference engine. */
function summarizeScenes(luminary: Luminary): SceneSummary[] {
    const scenes = (luminary.scenes ?? []) as unknown as {
        sceneDesign?: string;
        sceneCategory?: string;
        getAssets?: () => Asset[];
    }[];

    return scenes.map((scene) => {
        const assets = typeof scene.getAssets === 'function' ? (scene.getAssets() ?? []) : [];
        const texts = assets
            .filter((asset) => VideoData.isTextAsset(asset))
            .map((asset) => {
                const textAsset = asset as {
                    name?: string;
                    text?: string;
                    textSize?: number;
                    isEditable?: boolean;
                };
                return {
                    name: textAsset.name ?? '',
                    // Decorative copy baked into a design — a quote layout's oversized glyph, a
                    // caption slot's placeholder — ships as a named text asset the author cannot
                    // edit. It is not text the reference's author wrote, so counting it would cost
                    // a genuinely text-free reference its "leave every text asset empty" rule.
                    text: textAsset.isEditable === false ? '' : (textAsset.text ?? '').trim(),
                    size: Math.round(textAsset.textSize ?? 0),
                };
            });
        return {
            design: scene.sceneDesign ?? '(unknown design)',
            category: scene.sceneCategory,
            texts,
        };
    });
}

/** `design x count` in first-appearance order, so the agent can reuse the reference's own designs. */
function describeDesigns(scenes: SceneSummary[]): string {
    const counts = new Map<string, number>();
    scenes.forEach((scene) => counts.set(scene.design, (counts.get(scene.design) ?? 0) + 1));
    const designs = [...counts.entries()]
        .map(([design, count]) => `${design} x${count}`)
        .join(', ');
    return `- Scene designs used: ${designs}`;
}

/**
 * Whether the reference shows on-screen text at all.
 *
 * Worth stating as a video-level fact because the absence of text is invisible per-scene: a design
 * ships text slots whether or not the video fills them, so a reader working scene by scene sees
 * fillable slots throughout and no signal that this video never uses them.
 */
function describeTextUsage(scenes: SceneSummary[]): string[] {
    const withText = scenes.filter((scene) => scene.texts.some((text) => text.text.length > 0));

    if (withText.length === 0) {
        return [
            `- On-screen text: this reference carries NO on-screen text — all ${scenes.length} scenes leave their text assets empty.`,
            '  Leave every text asset empty in your video too. The designs have text slots, but the reference does not use them.',
        ];
    }
    return [`- On-screen text: ${withText.length} of ${scenes.length} scenes carry text.`];
}

/**
 * The caption placement as one of the three values `set_captions` accepts, classified by which
 * third of the frame the caption box's center sits in. Null when the geometry can't be read.
 *
 * Deliberately coarse: the tool only offers top/center/bottom, so reporting the raw y would name a
 * placement the agent has no way to ask for. Captions default to the bottom, which makes any other
 * answer an override worth reproducing.
 */
function describeCaptionPosition(asset: {
    approximateMaxBounds?: { y?: number; height?: number };
    root?: { referenceResolution?: { height?: number } };
}): string | null {
    const frameHeight = asset.root?.referenceResolution?.height;
    const boxTop = asset.approximateMaxBounds?.y;
    const boxHeight = asset.approximateMaxBounds?.height;
    if (
        typeof frameHeight !== 'number' ||
        frameHeight <= 0 ||
        typeof boxTop !== 'number' ||
        typeof boxHeight !== 'number'
    ) {
        return null;
    }

    const center = (boxTop + boxHeight / 2) / frameHeight;
    if (center < 1 / 3) {
        return 'top';
    }
    return center > 2 / 3 ? 'bottom' : 'center';
}

/**
 * How many scenes actually render the global caption track.
 *
 * `scene.displayCaptions` suppresses the track on one scene while leaving it on for the video, so a
 * reference can deliberately caption only part of itself — a distinction that vanishes into a
 * single global "captions are on" and then gets applied to every scene of the imitation.
 *
 * Both halves have to hold for a scene to actually render captions. `displayCaptions` answers only
 * the per-scene half: it returns the scene's own override, and otherwise defaults to true without
 * ever consulting the group. Visibility is per scene GROUP and the editor lets a user diverge it,
 * so a scene in a track-off group still reports true — counting it would report full coverage for a
 * reference that captions only one of its groups.
 */
function countCaptionedScenes(luminary: Luminary): { shown: number; total: number } {
    const scenes = (luminary.scenes ?? []) as unknown as {
        displayCaptions?: boolean;
        sceneGroup?: SceneGroup;
    }[];
    const inVoicedrivenGroup = scenes.filter((scene) => isVoicedriven(scene.sceneGroup));
    return {
        shown: inVoicedrivenGroup.filter(
            (scene) =>
                scene.sceneGroup?.captions?.visible === true && scene.displayCaptions === true,
        ).length,
        total: inVoicedrivenGroup.length,
    };
}

/**
 * The same test `isVoiceDrivenSceneGroup` applies, inlined: importing that selector pulls in the
 * luminary barrel and, through it, most of the editor — a heavy dependency for one two-field
 * predicate, in a module whose whole job is to stay loadable against a bare reference engine.
 */
const isVoicedriven = (sceneGroup: SceneGroup | undefined): boolean =>
    Boolean(sceneGroup?.mediaAsset) || Boolean(sceneGroup?.voiceoverAsset);

/**
 * Whether the reference captions its voiceover, and how.
 *
 * Video-level for the same reason the text-usage rule is: captions are a single global track, and
 * a reference that deliberately shows none looks identical, scene by scene, to one whose captions
 * simply weren't described — every voicedriven design can carry the track either way. So the OFF
 * case is stated as loudly as the ON case; it is a choice to reproduce, not an omission.
 *
 * Omitted entirely for a text-on-media reference, which has no voiceover to caption: reporting
 * "captions off" there would invite the agent to turn on a track that cannot exist.
 */
function describeCaptions(luminary: Luminary): string[] {
    const sceneGroups = (luminary.sceneGroups ?? []) as SceneGroup[];
    const voicedriven = sceneGroups.filter((sceneGroup) => isVoicedriven(sceneGroup));
    if (voicedriven.length === 0) {
        return [];
    }

    // Any one visible track describes the video: set_captions writes the design, size, colors and
    // font across every voicedriven group at once.
    const captioned = voicedriven.find((sceneGroup) => sceneGroup.captions?.visible);
    if (!captioned) {
        return [
            '- Captions: OFF. This reference is voicedriven and deliberately shows NO captions.',
            '  Leave captions off in your video too — new videos inherit the last-used setting, ' +
                `so if yours has them on, turn them off with \`${TOOL_NAMES.setCaptions}\`.`,
        ];
    }

    const { sceneDesignId, asset } = captioned.captions;
    const position = asset ? describeCaptionPosition(asset) : null;
    const details = [
        sceneDesignId ? `design ${sceneDesignId}` : null,
        typeof asset?.textSize === 'number' ? `size ${Math.round(asset.textSize)}` : null,
        typeof asset?.normalStyle?.fill === 'number'
            ? `color ${intToHex(asset.normalStyle.fill)}`
            : null,
        position ? `position ${position}` : null,
    ].filter((detail): detail is string => detail !== null);

    const lines = [
        `- Captions: ON${details.length > 0 ? ` — ${details.join(', ')}` : ''}.`,
        `  Match this with \`${TOOL_NAMES.setCaptions}\`; the design, size, color and position are ` +
            'global, so one call covers the whole video.',
    ];

    // Only worth saying when coverage is actually partial: on a fully captioned reference this
    // would just invite a scene_ids call that changes nothing.
    const { shown, total } = countCaptionedScenes(luminary);
    if (total > 0 && shown < total) {
        lines.push(
            `  Shown on ${shown} of ${total} voicedriven scenes, NOT all of them — the reference turns captions off on the rest.`,
            '  Reproduce that: turn captions on, then pass `scene_ids` to ' +
                `\`${TOOL_NAMES.setCaptions}\` to hide them on the scenes playing the same role.`,
        );
    }
    return lines;
}

/**
 * The scene that closes the video. `sceneCategory: outro` is the reliable marker when present, but
 * plenty of references close on an untagged scene ("Thank you for watching" tagged `common`) — and
 * a rule keyed only on the marker then matches nothing and the closer is silently dropped. So name
 * the tagged outro when there is one and the final scene otherwise, always with its text.
 */
function describeClosingScene(scenes: SceneSummary[]): string[] {
    const outro = scenes.find((scene) => scene.category === VideoData.CATEGORY_OUTRO);
    const closing = outro ?? scenes[scenes.length - 1];
    if (!closing) {
        return [];
    }

    const text = closing.texts
        .filter((entry) => entry.text.length > 0)
        .map((entry) => `${entry.name}: ${JSON.stringify(entry.text)}`)
        .join('; ');

    if (outro) {
        return [
            `- Closing scene: tagged \`sceneCategory: outro\`, design ${closing.design}.${text ? ` Text — ${text}` : ''}`,
        ];
    }
    return [
        `- Closing scene: No scene is tagged \`sceneCategory: outro\`, so match the FINAL scene instead — design ${closing.design}.${text ? ` Text — ${text}` : ''}`,
        '  Reproduce this closing scene in your video; do not end on a body scene.',
    ];
}

const INSIGHTS_HEADER =
    '## Reference Style Summary\n' +
    '_Video-level facts about the reference below. Per-asset styling is marked on the property ' +
    'lines themselves — see `changed from design default`._\n';

/**
 * Builds the summary block. Never throws: the annotation it precedes is the tool's real payload,
 * so a malformed reference loses its summary rather than the whole fetch.
 */
export function extractReferenceInsights(luminary: Luminary): string {
    try {
        const scenes = summarizeScenes(luminary);
        if (scenes.length === 0) {
            return '';
        }

        const lines = [
            describeDesigns(scenes),
            ...describeTextUsage(scenes),
            ...describeCaptions(luminary),
            ...describeClosingScene(scenes),
        ];

        return `${INSIGHTS_HEADER}${lines.join('\n')}\n`;
    } catch (error) {
        ErrorReporter.capture(error, { fingerprint: ['reference-video-insights'] });
        return '';
    }
}
